import {
  getAssignedCards,
  getCard,
  getCardAttachment,
  getCards,
  getMyCards,
  getSubTasks,
  getUserCards,
} from "../services/apiCards";
import { getComments } from "../services/apiComment";
import { hasQueryValue, useMappedQuery } from "./queryUtils";

export const invalidateCardQueries = (
  queryClient,
  boardId,
  cardId = null,
  milestone = null
) => {
  const queriesToInvalidate = [
    { queryKey: ["cards", boardId] },
    { queryKey: ["cards", String(boardId)] },
    ...(cardId ? [{ queryKey: ["card", String(cardId)] }] : []),
    { queryKey: ["boardName", boardId] },
    { queryKey: ["labels", boardId] },
    ...(milestone
      ? [
          { queryKey: ["milestone_history", milestone] },
          { queryKey: ["epic_id", milestone] },
        ]
      : []),
    { predicate: (query) => query?.queryKey[0] === "mineCards" },
    { predicate: (query) => query?.queryKey[0] === "userCards" },
    { predicate: (query) => query?.queryKey[0] === "assignedCards" },
  ];

  queriesToInvalidate.forEach((query) => {
    queryClient.invalidateQueries(query);
  });
};

export function useCards(id) {
  return useMappedQuery({
    queryKey: ["cards", id],
    queryFn: () => getCards(id),
    enabled: hasQueryValue(id),
    dataKey: "cards",
  });
}

export function useUserCards(id, board, workflow) {
  return useMappedQuery({
    queryKey: ["userCards", id],
    queryFn: () => getUserCards(id, board, workflow),
    enabled: hasQueryValue(id),
    dataKey: "cards",
  });
}

export function useComment(id) {
  return useMappedQuery({
    queryKey: ["comment", id],
    queryFn: () => getComments(id),
    dataKey: "comment",
  });
}

export function useCard(id) {
  return useMappedQuery({
    queryKey: ["card", id],
    queryFn: () => getCard(id),
    enabled: hasQueryValue(id),
    retry: 1,
    dataKey: "card",
  });
}

export function useCardAttachment(id) {
  return useMappedQuery({
    queryKey: ["cardAttachment", id],
    queryFn: () => getCardAttachment(id),
    dataKey: "cardAttachment",
  });
}

export function useMineCards(
  user,
  checkedList,
  epicSearch,
  reporterCards,
  workflow_id,
  unassigned_task,
  boards
) {
  return useMappedQuery({
    queryKey: [
      "mineCards",
      workflow_id,
      user[0]?.user_id || "",
      user[1]?.user_id || "",
      boards,
      unassigned_task,
    ],
    queryFn: () =>
      getMyCards(
        user,
        checkedList,
        epicSearch,
        reporterCards,
        workflow_id,
        unassigned_task,
        boards
      ),
    enabled: hasQueryValue(workflow_id),
  });
}

export function useSubtasks(cardId) {
  return useMappedQuery({
    queryKey: ["subTasks", cardId],
    queryFn: () => getSubTasks(cardId),
    enabled: hasQueryValue(cardId),
    dataKey: "subtasks",
  });
}

export function useGetAssignedCards(userId) {
  const {
    assignedCards,
    isLoading,
    error,
    isError,
    refetch,
  } = useMappedQuery({
    queryKey: ["assignedCards", userId],
    queryFn: () => getAssignedCards(userId),
    enabled: hasQueryValue(userId),
    dataKey: "assignedCards",
  });

  return {
    assignedCards,
    isLoading,
    error,
    isError,
    refetchAssignedCards: refetch,
  };
}
